import { type ReactNode } from "react";
import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface StatPill {
  value: number | string;
  label: string;
  tone?: "neutral" | "success" | "warning";
  icon?: LucideIcon;
}

interface PageHeaderProps {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  tone?: "primary" | "zinc";
  leading?: ReactNode;
  titleExtra?: ReactNode;
  titleBadge?: ReactNode;
  action?: ReactNode;
  stats?: StatPill[];
  secondaryGlow?: boolean;
}

const toneClasses = {
  primary: {
    gradient: "from-primary/10 via-primary/5 to-transparent dark:from-transparent dark:via-transparent",
    glowPrimary: "bg-primary/10 dark:bg-transparent",
    glowSecondary: "bg-primary/8 dark:bg-transparent",
    iconBox: "bg-primary/15 dark:bg-primary/8 ring-1 ring-primary/20 dark:ring-primary/10",
    iconColor: "text-primary",
  },
  zinc: {
    gradient: "from-zinc-400/10 via-zinc-400/5 to-transparent",
    glowPrimary: "bg-zinc-400/10",
    glowSecondary: "bg-zinc-400/8",
    iconBox: "bg-zinc-400/15 ring-1 ring-zinc-400/20",
    iconColor: "text-zinc-500 dark:text-zinc-400",
  },
} as const;

const pillToneClasses = {
  neutral: "bg-background/60 text-foreground ring-1 ring-border/60",
  success: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-1 ring-emerald-500/20",
  warning: "bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-1 ring-amber-500/20",
} as const;

function StatPillView({ value, label, tone = "neutral", icon: Icon }: StatPill) {
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
        pillToneClasses[tone],
      )}
    >
      {Icon && <Icon className="h-3 w-3 text-muted-foreground" />}
      <span className="font-bold">{value}</span>
      <span className={tone === "neutral" ? "text-muted-foreground" : undefined}>{label}</span>
    </div>
  );
}

export function PageHeader({
  icon: Icon,
  title,
  subtitle,
  tone = "primary",
  leading,
  titleExtra,
  titleBadge,
  action,
  stats,
  secondaryGlow = true,
}: PageHeaderProps) {
  const t = toneClasses[tone];
  const hasComplexLayout = Boolean(stats || action || leading);

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border bg-gradient-to-br px-6 py-5",
        t.gradient,
      )}
    >
      <div
        className={cn(
          "absolute right-0 top-0 h-32 w-32 translate-x-8 -translate-y-8 rounded-full blur-2xl",
          t.glowPrimary,
        )}
      />
      {secondaryGlow && (
        <div
          className={cn(
            "absolute right-16 bottom-0 h-20 w-20 translate-y-6 rounded-full blur-xl",
            t.glowSecondary,
          )}
        />
      )}
      <div
        className={cn(
          "relative flex",
          leading
            ? "items-start gap-4"
            : hasComplexLayout
              ? "flex-col items-start gap-3 sm:flex-row sm:justify-between"
              : "items-center",
        )}
      >
        {leading}
        <div className={cn(leading ? "min-w-0" : undefined, titleBadge && "flex-1", "space-y-2")}>
          <div className="flex items-center gap-3">
            <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", t.iconBox)}>
              <Icon className={cn("h-5 w-5", t.iconColor)} />
            </div>
            <div className={cn("min-w-0", titleBadge && "flex-1")}>
              <div className={cn("flex items-center", titleBadge && "justify-between")}>
                <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
                {titleBadge}
              </div>
              {titleExtra ? (
                <div className="flex flex-wrap items-center gap-2">{titleExtra}</div>
              ) : (
                subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>
              )}
            </div>
          </div>
          {stats && stats.length > 0 && (
            <div
              className={cn(
                "flex flex-wrap items-center gap-3",
                leading ? "pl-[52px]" : "pl-1 pt-1",
              )}
            >
              {stats.map((stat, i) => (
                <StatPillView key={i} {...stat} />
              ))}
            </div>
          )}
        </div>
        {action}
      </div>
    </div>
  );
}
